import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { Button } from '@/UI-KIT/Button/Button'
import { Typography } from '@/UI-KIT/Typography/Typography'
import { useScrollLock } from '@/utils/useScrollLock'

import { CalendlyStep } from './CalendlyStep'
import { ROLES, TEAM_SIZES, useDemoStore } from './DemoStore'
import styles from './DemoDialog.module.scss'

/**
 * Four fields that matter, because every extra one costs a lead: who you are, how to reach you, and
 * what you sell. Everything else is optional and says so.
 *
 * The honeypot input is in the tab order's shadow (`tabIndex={-1}`, hidden from assistive tech) and
 * off-screen rather than `display:none` — bots skip what is not rendered and fill what is.
 */
export const DemoDialog = observer(({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()
  const store = useDemoStore()

  /* Диалог БОЛЬШЕ НЕ ЗАКРЫВАЕТСЯ САМ. Раньше он показывал благодарность и уходил через 2,2 секунды;
     теперь на его месте календарь, и закрывать окно, в котором человек выбирает время, нельзя.
     Уходит он сам — крестиком или по клику мимо, как любой другой диалог. */

  /* Лендинг под заявкой не листается пальцем (мобильный аудит 2026-09-28, sweep10): свайп в конце
     формы уводил страницу под окном. Только на сенсорном экране — на десктопе окно страницу не
     блокировало и не начинает (см. useScrollLock). */
  useScrollLock(true, 'touch')

  return (
    <div
      className={styles.veil}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form
        /* `booking` — шаг календаря: на телефоне он занимает весь экран, и прокручивается только сам
           календарь, а не окно вокруг него (DemoDialog.module.scss). */
        className={cn(styles.card, { [styles.booking as string]: store.booking })}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demoTitle"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void store.submit()
        }}
      >
        <button type="button" className={styles.close} onClick={onClose} aria-label={t('ui.common.close')}>
          ×
        </button>

        {store.booking ? (
          /* Заявка принята — на месте формы календарь. Именно сейчас человек заинтересован
             больше всего, и просить его ждать письма значит терять этот момент. */
          <CalendlyStep name={store.name} email={store.email} direct={store.direct} />
        ) : (
          <>
          <div className={styles.kick}>{t('landing.demo.kick')}</div>
          <Typography variant="h3" id="demoTitle">
            {t('landing.demo.title')}
          </Typography>
          <p className={styles.sub}>{t('landing.demo.sub')}</p>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>{t('landing.demo.fields.name')}</span>
              <input
                name="name"
                autoComplete="name"
                placeholder={t('landing.demo.placeholders.name')}
                value={store.name}
                onChange={(event) => store.setField('name', event.target.value)}
              />
            </label>

            <label className={styles.field}>
              <span>
                {t('landing.demo.fields.role')} <i>*</i>
              </span>
              <select
                name="role"
                className={cn({ [styles.bad as string]: store.error === 'role' })}
                value={store.role}
                onChange={(event) => store.setField('role', event.target.value)}
              >
                <option value="">{t('landing.demo.selectOne')}</option>
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {t(`landing.demo.roles.${role}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>
                {t('landing.demo.fields.email')} <i>*</i>
              </span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                className={cn({ [styles.bad as string]: store.error === 'email' })}
                placeholder={t('landing.demo.placeholders.email')}
                value={store.email}
                onChange={(event) => store.setField('email', event.target.value)}
              />
            </label>

            <label className={styles.field}>
              <span>{t('landing.demo.fields.company')}</span>
              <input
                name="company"
                autoComplete="organization"
                placeholder={t('landing.demo.placeholders.company')}
                value={store.company}
                onChange={(event) => store.setField('company', event.target.value)}
              />
            </label>
          </div>

          <label className={styles.field}>
            <span>
              {t('landing.demo.fields.teamSize')} <em>{t('landing.demo.optional')}</em>
            </span>
            <select
              name="team_size"
              value={store.teamSize}
              onChange={(event) => store.setField('teamSize', event.target.value)}
            >
              <option value="">{t('landing.demo.preferNotToSay')}</option>
              {TEAM_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t(`landing.demo.teamSizes.${size}`)}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span>{t('landing.demo.fields.note')}</span>
            <textarea
              name="note"
              rows={3}
              placeholder={t('landing.demo.placeholders.note')}
              value={store.note}
              onChange={(event) => store.setField('note', event.target.value)}
            />
          </label>

          <input
            className={styles.honeypot}
            name="website_url"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={store.websiteUrl}
            onChange={(event) => store.setField('websiteUrl', event.target.value)}
          />

          {/* Кнопка и её ответ — одним блоком: на телефоне он прилипает к низу листа, и «Request a
              demo» с ошибкой под ним видны, где бы ни стояла прокрутка формы. На десктопе обёртка
              без правил и ничего не двигает. */}
          <div className={styles.actions}>
            <Button type="submit" colorScheme="blue" size="lg" className={styles.submit} disabled={store.sending || store.sent}>
              {store.sent
                ? t('landing.demo.sent')
                : store.sending
                  ? t('landing.demo.sending')
                  : t('landing.demo.submit')}
            </Button>

            {store.sent ? <div className={cn(styles.note, styles.ok)}>{t('landing.demo.thanks')}</div> : null}
            {store.error === 'email' ? <div className={cn(styles.note, styles.err)}>{t('landing.demo.errors.email')}</div> : null}
            {store.error === 'role' ? <div className={cn(styles.note, styles.err)}>{t('landing.demo.errors.role')}</div> : null}
            {/* Две строки, а не одна через разделитель: ответ сервера и наш запасной адрес это разные
                сообщения, и склеивать их знаком значило бы выдавать за одну фразу. Заодно уходит
                последнее длинное тире, зашитое в разметку, а не в переводы. */}
            {store.error === 'server' ? (
              <div className={cn(styles.note, styles.err)}>
                <span className={styles.noteLine}>{store.serverMessage}</span>
                <span className={styles.noteLine}>{t('landing.demo.errors.orWrite')}</span>
              </div>
            ) : null}
          </div>
          </>
        )}

        <p className={styles.fine}>{t('landing.demo.fine')}</p>
      </form>
    </div>
  )
})
